import { FC, useCallback, useEffect, useState, useMemo } from "react";
import { Tabs, Tab } from "@appkit4/react-components";
import { useParams } from "react-router-dom";
import { useAtomValue } from "jotai";
import { filterAtom } from "jotai/store";
import { LoaderWrapper } from "components/common/helpers";
import { Chevron, TechnicalRoles, TestDetails, TestIcon } from "routes/analysis/helpers/analysisHelpers";
import { FetchRoleTests } from "../queries/role";
import { Role } from "routes/analysis/types/analysis";
import RoleMatches from "./RoleMatches";

const businessProcesses = ["FIN", "HR", "IT", "MMI", "O2C", "P2P", "PB", "SB"];

const RoleTestList: FC<{ role: Role }> = ({ role }) => {
  const { analysisId } = useParams();
  const [selectedTest, setSelectedTest] = useState<string>();
  const [activeIndex, setActiveIndex] = useState<number>(8);
  const userFilter = useAtomValue(filterAtom);
  const { data, isPending, error } = FetchRoleTests(analysisId, role.roleId, userFilter, role.isComposite);
  const [subIndex, setSubIndex] = useState(0);

  const getTestCount = useCallback((id: string) => {
    return (data?.filter(test => test.businessProcess.split(":")[0] === id) || []).length || 0;
  }, [data]);

  const getBPLabel = (id: string) => (
    <span>
      {id} <span className="ap-item-count">{getTestCount(id)}</span>
    </span>
  );

  const getProcessWithTests = useCallback(() => {
    if (!data) return 8;
    for (let i = 0; i < businessProcesses.length; i++) {
      if (getTestCount(businessProcesses[i]) > 0) return i;
    }
    return 8;
  }, [data, getTestCount]);

  useEffect(() => {
    if (data && !isPending) {
      setActiveIndex(getProcessWithTests());
    }
  }, [data, isPending, getProcessWithTests]);

  useEffect(() => {
    document.getElementById("process")?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [activeIndex]);

  const filteredTests = useMemo(() => {
    return (data?.filter(test => test.businessProcess.split(":")[0] === businessProcesses[activeIndex]) || [])
      .sort((a, b) => a.identifier.localeCompare(b.identifier));
  }, [data, activeIndex]);

  return (
    <LoaderWrapper loading={[isPending]} errors={[error]} inline>
      <div className="accordion-wrapper ap-accordion-open" id="process" style={{ scrollMarginTop: 80 }}>
        <div className="tabs-wrapper">
          <Tabs activeIndex={activeIndex} onTabChange={setActiveIndex} aria-label="Business Process Tabs">
            {businessProcesses.map((bp, index) => (
              <Tab label={getBPLabel(bp)} value={index.toString()} disabled={getTestCount(bp) === 0} key={bp} aria-label={`Tab for ${bp}`} />
            ))}
            <Tab label="Technical roles" value="8" disabled={!role.isComposite} aria-label="Tab for Technical roles"></Tab>
          </Tabs>
        </div>
        {activeIndex < 8 ? (
          <>
            <div className="ap-list-header ap-business-process-test-header flex gap-4 p-4 pt-6 pb-6 items-center">
              <div className="basis-12"></div>
              <div className="basis-96">Test</div>
              <div>Test type</div>
            </div>
            {filteredTests.map(test => (
              <div className={`list-row-1`} key={test.testId}>
                <div className="flex items-center p-4 gap-4">
                  <div className="basis-12">
                    <Chevron
                      open={test.testId === selectedTest}
                      onClick={() => {
                        setSubIndex(0);
                        test.testId === selectedTest
                          ? setSelectedTest(undefined)
                          : setSelectedTest(test.testId);
                      }}
                      aria-label={test.testId === selectedTest ? `Collapse test ${test.identifier}` : `Expand test ${test.identifier}`}
                    />
                  </div>
                  <div className="basis-96">
                    <b>{test.identifier}</b>
                    <p>
                      {(test.functions ?? []).map(m => (
                        <span className="ap-function-description" key={m.functionId || m.description}>{m.description}</span>
                      ))}
                    </p>
                  </div>
                  <div>
                    <TestIcon type={test.type} />
                  </div>
                </div>
                {test.testId === selectedTest && (
                  <>
                    <div className="tabs-wrapper">
                      <Tabs activeIndex={subIndex} onTabChange={setSubIndex} aria-label={`Test details tabs for ${test.identifier}`}>
                        <Tab label="Matches" value="0" aria-label={`Matches for ${test.identifier}`}></Tab>
                        <Tab label="Test details" value="1" aria-label={`Details for ${test.identifier}`}></Tab>
                      </Tabs>
                    </div>
                    {subIndex === 0 ? (
                      <RoleMatches role={role} test={test} />
                    ) : (
                      <TestDetails test={test} />
                    )}
                  </>
                )}
              </div>
            ))}
          </>
        ) : (
          <TechnicalRoles roles={role.roles || []} />
        )}
      </div>
    </LoaderWrapper>
  );
};

export default RoleTestList;
